// The menu bar, in the order and wording of MainWindow._build_menus (frontend/ui/main_window.py).
// "Exit", "Licence…" and "Compile Hailo Bundle to .hef…" are desktop-only (parity rows A8, A9, E6).
import { SHORTCUTS, displayKey } from "./shortcuts";

export interface MenuItem {
  /** Action id handled by the shell; undefined while the feature belongs to a later phase. */
  action?: string;
  label: string;
  /** shortcuts.ts id, shown as the key hint. */
  shortcut?: string;
  separator?: boolean;
  checkable?: boolean;
  adminOnly?: boolean;
  /** Needs the annotation view (Phase 3+). */
  annotateOnly?: boolean;
  /** Needs a selected project (dashboard / annotation). */
  needsProject?: boolean;
  /** Changes data, so a read-only account (a viewer) cannot use it. */
  editOnly?: boolean;
  /** Licensed feature it needs (backend/app/licensing/features.py); greyed out with the Pro tooltip when closed. */
  feature?: string;
  /** The web's wording (sentence case) where it differs from the desktop's `label`, which the parity tests compare. */
  display?: string;
}
export interface Menu { label: string; items: MenuItem[] }

const sep: MenuItem = { label: "", separator: true };

export const MENUS: Menu[] = [
  { label: "File", items: [
    { action: "nav.dashboard", label: "Back to Dashboard", display: "Back to dashboard", shortcut: "app.dashboard" },
    { action: "admin.open", label: "Admin Dashboard", display: "Administration", adminOnly: true },
    { action: "password.change", label: "Change Password", display: "Change password" },
    { action: "logout", label: "Logout", display: "Sign out" },
  ] },
  { label: "Tools", items: [
    { action: "review.reset", editOnly: true, label: "Reset Review State…", display: "Reset review state…" },
    { action: "review.clear", editOnly: true, label: "Clear Unreviewed Annotations…", display: "Clear unreviewed annotations…" },
    sep,
    { action: "export.measurements", label: "Export Measurements to CSV…", display: "Export measurements to CSV…", feature: "measurement_export" },
    { action: "export.measurements.xlsx", label: "Export Measurements to Excel (with photos)…", display: "Export measurements to Excel (with photos)…", feature: "measurement_export" },
  ] },
  { label: "View", items: [
    { action: "theme.toggle", label: "Toggle Theme", display: "Toggle theme", shortcut: "app.theme" },
    { action: "view.fit", label: "Fit Image", display: "Fit image", shortcut: "view.fit", annotateOnly: true },
    sep,
    { action: "view.depth", label: "Depth View", display: "Depth view", shortcut: "view.depth", checkable: true, annotateOnly: true, feature: "tof" },
  ] },
  { label: "Help", items: [
    { action: "help.shortcuts", label: "Keyboard Shortcuts…", display: "Keyboard shortcuts…", shortcut: "help.shortcuts" },
  ] },
  { label: "Annotation", items: [
    { action: "tool.bbox", editOnly: true, label: "BBox Tool", display: "Box tool", shortcut: "tool.bbox", annotateOnly: true },
    { action: "tool.polygon", editOnly: true, label: "Polygon Tool", display: "Polygon tool", shortcut: "tool.polygon", annotateOnly: true },
    { action: "tool.obb", editOnly: true, label: "Rotated Box Tool", display: "Rotated box tool", shortcut: "tool.obb", annotateOnly: true },
    { action: "tool.select", label: "Select/Pan", display: "Select / pan", shortcut: "tool.select", annotateOnly: true },
    sep,
    { action: "edit.undo", editOnly: true, label: "Undo", shortcut: "edit.undo", annotateOnly: true },
    { action: "edit.redo", editOnly: true, label: "Redo", shortcut: "edit.redo", annotateOnly: true },
    sep,
    { action: "edit.select_all", editOnly: true, label: "Select All", display: "Select all", shortcut: "edit.select_all", annotateOnly: true },
    { action: "edit.copy", editOnly: true, label: "Copy", shortcut: "edit.copy", annotateOnly: true },
    { action: "edit.paste", editOnly: true, label: "Paste (onto whichever image is open)", shortcut: "edit.paste", annotateOnly: true },
    { action: "edit.duplicate", editOnly: true, label: "Duplicate", shortcut: "edit.duplicate", annotateOnly: true },
    sep,
    { action: "edit.delete", editOnly: true, label: "Delete Selected", display: "Delete selected", shortcut: "edit.delete", annotateOnly: true },
    { action: "edit.save", editOnly: true, label: "Save", shortcut: "edit.save", annotateOnly: true },
    sep,
    { action: "review.mark", editOnly: true, label: "Mark Reviewed", display: "Mark reviewed", shortcut: "review.mark", annotateOnly: true },
    { action: "class.apply", editOnly: true, label: "Apply Class to Selected", display: "Apply class to selected", shortcut: "class.apply", annotateOnly: true },
    { action: "measure.selected", editOnly: true, label: "Measure Selected", display: "Measure selected", shortcut: "measure.selected", annotateOnly: true, feature: "measure" },
    sep,
    { action: "image.next", label: "Next Image", display: "Next image", shortcut: "image.next", annotateOnly: true },
    { action: "image.prev", label: "Previous Image", display: "Previous image", shortcut: "image.prev", annotateOnly: true },
  ] },
];

/** The catalogue by where the web shows it: the account menu, the project menu, and the annotation rail's More menu. */
export const menuItems = (label: string): MenuItem[] => MENUS.find((m) => m.label === label)?.items ?? [];
export const ACCOUNT_ITEMS = (): MenuItem[] => menuItems("File").filter((i) => i.action !== "nav.dashboard");
export const PROJECT_ITEMS = (): MenuItem[] => menuItems("Tools");
export const ANNOTATE_ITEMS = (): MenuItem[] => [
  ...menuItems("View").filter((i) => i.action === "view.fit" || i.action === "view.depth"), { label: "", separator: true }, ...menuItems("Annotation"),
];

/** "B", "Right / PgDown": the keys the web app binds for a shortcut id. */
export function hint(id: string): string {
  const s = SHORTCUTS.find((x) => x.id === id);
  return s ? s.webKeys.map((k) => displayKey(k)).join(" / ") : "";
}

/** Actions the shell implements today; everything else is shown disabled. */
export const IMPLEMENTED = new Set([
  "nav.dashboard", "admin.open", "password.change", "logout", "theme.toggle", "help.shortcuts",
  "review.reset", "review.clear", "export.measurements", "export.measurements.xlsx",
]);
